import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'
import { customerApi, resolveImageUrl } from '../services/api'

interface RestaurantInfo {
  restaurant_name?: string
  restaurant_name_ar?: string
  phone_number?: string
  address?: string
  address_ar?: string
  restaurant_logo_url?: string
  /** Logo shown on customer frontend (QR menu) home header. Separate from main restaurant logo. */
  customer_frontend_logo_url?: string
  /** Favicon for browser tab (customer menu, admin, waiter). */
  app_favicon_url?: string
  /** Loading icon shown while app is loading. */
  app_loading_icon_url?: string
  email?: string
  website?: string
  /** Google Maps or review page URL — used when customer taps "Leave us a review on Google Maps". */
  google_maps_review_url?: string
  currency?: string
  currency_symbol?: string
  currency_display_position?: 'left' | 'right'
  qr_menu_order_placing_enabled?: boolean
  /** VAT/tax rate as percentage (e.g. 15 for 15%). From restaurant settings. */
  tax_rate?: number
  /** Service charge as percentage (e.g. 10). From restaurant settings. */
  service_charge?: number
  /** Reservation: time slot duration in minutes (15, 30, 60). Default 30. */
  reservation_timeslot_duration_minutes?: number
  /** Reservation: minimum hours in advance to book. Default 2. */
  reservation_minimum_booking_hours?: number
  /** Global: per-day schedule. Keys 0–6 (Sun–Sat), value { open, close, closed }. */
  restaurant_schedule?: Record<string, { open: string; close: string; closed: boolean }>
  /** Global: holiday dates (closed) as YYYY-MM-DD[]. */
  restaurant_holidays?: string[]
}

export interface AllergenInfo {
  id: string
  name: string
  icon: string | null
}

interface RestaurantContextType {
  restaurantInfo: RestaurantInfo | null
  loading: boolean
  formatPrice: (price: number) => string
  orderPlacingEnabled: boolean
  /** VAT/tax rate as percentage (e.g. 15). From restaurant settings. Default 0 if not set. */
  taxRate: number
  /** Service charge as percentage. From restaurant settings. Default 0 if not set. */
  serviceChargeRate: number
  /** Map of allergen id -> { id, name, icon } for displaying allergen icons (name on hover). */
  allergenMap: Record<string, AllergenInfo>
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined)

export function RestaurantProvider({ children }: { children: ReactNode }) {
  const [restaurantInfo, setRestaurantInfo] = useState<RestaurantInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [allergenMap, setAllergenMap] = useState<Record<string, AllergenInfo>>({})

  useEffect(() => {
    loadRestaurantInfo()
    loadAllergens()
  }, [])

  const loadAllergens = async () => {
    try {
      const res = await customerApi.getAllergens()
      if (res.success && res.data && Array.isArray(res.data)) {
        const map: Record<string, AllergenInfo> = {}
        res.data.forEach((a) => {
          map[a.id] = { id: a.id, name: a.name, icon: a.icon }
        })
        setAllergenMap(map)
      }
    } catch (_) {}
  }

  // Apply cached favicon immediately for returning visitors
  useEffect(() => {
    try {
      const cached = localStorage.getItem('customer_app_favicon_url')
      if (cached) {
        const fullUrl = resolveImageUrl(cached)
        let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
        if (!link) {
          link = document.createElement('link')
          link.rel = 'icon'
          document.head.appendChild(link)
        }
        link.href = fullUrl
      }
    } catch {}
  }, [])

  const loadRestaurantInfo = async () => {
    try {
      const response = await customerApi.getRestaurantInfo()
      if (response.success && response.data) {
        setRestaurantInfo(response.data)
        if (response.data.app_loading_icon_url) {
          try {
            localStorage.setItem('customer_app_loading_icon_url', response.data.app_loading_icon_url)
          } catch {}
        }
        if (response.data.app_favicon_url) {
          try {
            localStorage.setItem('customer_app_favicon_url', response.data.app_favicon_url)
          } catch {}
        }
      }
    } catch (error) {
      console.error('Error loading restaurant info:', error)
    } finally {
      setLoading(false)
    }
  }

  // Set document favicon when restaurant info (with app_favicon_url) is available
  const faviconSet = useRef(false)
  useEffect(() => {
    const url = restaurantInfo?.app_favicon_url
    if (url && !faviconSet.current) {
      const fullUrl = resolveImageUrl(url)
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = fullUrl
      faviconSet.current = true
    }
  }, [restaurantInfo?.app_favicon_url])

  const formatPrice = (price: number): string => {
    if (!restaurantInfo) return `$${price.toFixed(2)}`

    const symbol = restaurantInfo.currency_symbol || '$'
    const position = restaurantInfo.currency_display_position || 'left'
    const formatted = price.toFixed(2)

    return position === 'left' ? `${symbol}${formatted}` : `${formatted} ${symbol}`
  }

  const orderPlacingEnabled = restaurantInfo?.qr_menu_order_placing_enabled !== false // Default to true if not set
  const taxRate = Number(restaurantInfo?.tax_rate) || 0
  const serviceChargeRate = Number(restaurantInfo?.service_charge) || 0

  // Full-screen loading with optional cached loading icon (for returning visitors)
  if (loading) {
    let loadingIconSrc = ''
    try {
      const cached = localStorage.getItem('customer_app_loading_icon_url')
      if (cached) loadingIconSrc = resolveImageUrl(cached)
    } catch {}
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="text-center">
          {loadingIconSrc ? (
            <img src={loadingIconSrc} alt="" className="max-h-24 w-auto mx-auto animate-pulse" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <span className="text-amber-600 text-2xl">🍽</span>
            </div>
          )}
          <p className="text-gray-500 text-sm mt-4">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <RestaurantContext.Provider value={{ restaurantInfo, loading, formatPrice, orderPlacingEnabled, taxRate, serviceChargeRate, allergenMap }}>
      {children}
    </RestaurantContext.Provider>
  )
}

export function useRestaurant() {
  const context = useContext(RestaurantContext)
  if (context === undefined) {
    throw new Error('useRestaurant must be used within a RestaurantProvider')
  }
  return context
}

